/**
 * CUSTO — o fechamento da compra para o escritório (2E): o que pagar e para
 * quem. Quem paga (a esposa ou a secretária do gestor, papel Consulta) paga em
 * contas diferentes, por isso cada um numa linha:
 *
 *   vendedor  — o líquido do romaneio (financeiro% + funrural% já descontados do bruto);
 *   Comissão do comprador (você paga) — valor por cabeça × cabeças, lançada na aba Pagamento;
 *   frete e outros gastos da ficha — em valor total, lista livre.
 *
 * Embaixo, o custo total da compra: total, por cabeça e por arroba.
 *
 * 3B · COM APARTAÇÃO (duas porteiras ou mais), um bloco por porteira: cabeças,
 * kg e arrobas, o valor ao vendedor daquela porteira, os gastos dela (frete de
 * cada caminhão; gasto sem porteira vira «gastos rateados» pelas cabeças) e:
 *   cliente  — a Comissão do cliente (você recebe); o papel do cliente fica no
 *              passo Romaneio, junto com os de compra e de venda (R2B);
 *   estoque  — o custo que entra no Meu estoque, sem comissão de cliente;
 *   revenda  — 3F: compra, venda, frete e gastos, comissão do comprador e
 *              ÁGIO, no total, por cabeça e por arroba — só gestor e Consulta.
 *
 * R2B (revisão de 06/10/2026) · «O QUE PAGAR E PARA QUEM» É SÓ A SUA PARTE: na
 * intermediação o cliente paga o vendedor e o frete da porteira dele direto.
 * A linha do vendedor mostra a sua parte; o gasto da porteira Cliente aparece
 * riscado, «a cargo do cliente» (`gastosDoGestor`, `divisaoDoPagamento`). O
 * custo do gestor fica separado do custo do cliente (conferência). Cada gasto
 * ganha favorecido, vencimento, «pago em» e comprovante, e se ANULA com motivo
 * (fica riscado) em vez de sumir no ✕.
 *
 * QUEM VÊ: gestor e Consulta, tudo. O comprador vê a ficha dele SEM nenhuma
 * comissão — nem a linha, nem um total que deixe deduzi-la: o total dele é
 * outra conta, e a tela diz só «Total». O vendedor não vê nada disto.
 *
 * Ficha encerrada fica «custo pendente» até alguém que grava (gestor ou
 * comprador; a Consulta só olha) tocar em «Custo fechado». Reabrir pede
 * motivo, e os dois ficam no histórico da ficha. Se o romaneio ou os gastos
 * mudarem depois de fechado, volta a «custo pendente» sozinho.
 */
import { useState } from 'react'
import { baixar, comprovantesDe, guardarArquivo, ArquivoRecusado, abrirComprovante, usarComprovantesDoServidor } from '../dados/arquivos'
import { mudarFicha, usarEstado } from '../dados/armazem'
import { comissaoDoCliente, quitacoesDa, recebimentosDaPorteira, usarPagamentos, vendaDaPorteira } from '../dados/pagamentos'
import { usarFerramenta, usarSessao } from '../dados/sessao'
import { calcular, custoPendente, fechamentoDaCompra, totalSemComissao, type Fechamento } from '../dominio/contas'
import { contasPorPorteira, divisaoDoPagamento, gastosDoGestor, linhasDaRevenda, porteirasDa, temApartacao, type Apartacao, type ContaDaPorteira } from '../dominio/destinos'
import { comissaoPagaFora } from '../dominio/financeiro'
import { situacaoDoRecebimento } from '../dominio/revenda'
import { abasDoFechamento, destinoInterno, lugar, periodo, rotuloDoDestino } from '../dominio/exportar'
import { agora, dataBR, hoje, kg, lerNumero, novoId, num, quando, reais } from '../dominio/numeros'
import { montarXlsx, nomeDeArquivo } from '../dominio/planilha'
import { NOME_COMISSAO_CLIENTE, NOME_COMISSAO_COMPRADOR, TIPOS_DE_PORTEIRA, type Comprovante, type Custo as Gasto, type Ficha } from '../dominio/tipos'
import { avisar, Campo, confirmar, Dica, Escolha, pedirMotivoNaJanela, Tarja, type Ir } from '../ui'
import { Folha } from './Romaneio'
import { CatiraNaFicha } from './Catira'
import { pode } from '../dominio/permissoes'

/** valor de cada linha em letra grande: é o número que o escritório vai digitar no banco */
const GRANDE = { fontSize: '1.15rem' }
const XLSX = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

export function Custo({ ficha, ir }: { ficha: Ficha; ir: Ir }) {
  const { ajustes } = usarEstado()
  const sessao = usarSessao()
  const pg = usarPagamentos()
  const papel = sessao.tipo === 'dentro' ? sessao.eu.pessoa.papel : 'consulta'
  const quem = sessao.tipo === 'dentro' ? sessao.eu.pessoa.nome : ''
  const verComissao = papel === 'gestor' || papel === 'consulta'
  // 3F: o preço de venda e o ágio, só gestor e Consulta (o comprador nem tem a tabela no aparelho)
  const verVenda = pode(papel, 'verVenda')
  // a Consulta só olha; o comprador só tem no aparelho as fichas dele (3E: a matriz única)
  const podeGravar = pode(papel, 'fecharCusto') && pode(papel, 'lancarGasto') && ficha.situacao !== 'cancelada'
  // R2B: marcar o gasto como pago (mora na ficha: gestor e o comprador dela; a Consulta vê)
  const podePagar = pode(papel, 'pagarGasto', { encerrada: ficha.situacao === 'encerrada' }) && ficha.situacao !== 'cancelada'
  const comCatira = usarFerramenta('catira')
  const [descricao, setDescricao] = useState('')
  const [valor, setValor] = useState('')
  // 3B: de que porteira é o gasto ('' = divide pelas cabeças)
  const [destinoGasto, setDestinoGasto] = useState('')

  const r = calcular(ficha)
  const comissao = pg.comissoes[ficha.id] ?? null
  const dv = divisaoDoPagamento(ficha)
  const fe = fechamentoDaCompra(ficha, r, {
    verComissao, comissao, quitacoes: quitacoesDa(pg, ficha.id), aCargoDoCliente: dv.aCargoDoCliente,
    gastosDoGestor: gastosDoGestor(ficha), comissaoPagaFora: comissaoPagaFora(pg, ficha.id),
  })
  const apartando = temApartacao(ficha)
  const ap = contasPorPorteira(ficha, {
    verComissao, comissao, comissaoCliente: (d) => comissaoDoCliente(pg, ficha.id, d),
    vendaDe: verVenda ? (d) => vendaDaPorteira(pg, ficha.id, d) : undefined,
  })
  const pendente = custoPendente(ficha, r)
  const fechado = ficha.situacao === 'encerrada' && !pendente
  // fechado, a lista trava: mudar (ou anular) gasto é reabrir o custo, com motivo
  const podeLancar = podeGravar && !fechado
  const nome = nomeDeArquivo('custo', ficha.numero, ficha.produtor.propriedade || ficha.produtor.nome)
  // etapa 1: a comissão era lançada como gasto livre. Com a da aba Pagamento, contaria duas vezes.
  const comissaoNosGastos = verComissao && !!fe.comissao && fe.gastos.some((x) => /comiss/i.test(x.descricao))
  const nomeDaPorteiraDe = (id?: string) => ap.porteiras.find((p) => p.id === id && p.id !== null)?.nome
  const temCliente = fe.vendedor.aCargoDoCliente > 0 || fe.custoDoCliente > 0
  // R2B: na ficha de Venda o dinheiro do gado ENTRA — o comprador paga; o resultado é venda − gastos − comissão
  const venda = ficha.finalidade === 'Venda'
  const resultadoDaVenda = venda ? Math.round((r.liquido - fe.totalGastos - (fe.comissao?.valor ?? 0)) * 100) / 100 : 0

  function lancar() {
    const v = lerNumero(valor)
    if (!descricao.trim()) { window.alert('Descreva o gasto (ex.: frete).'); return }
    if (!(v && v > 0)) { window.alert('Digite o valor total do gasto.'); return }
    const destinoId = apartando && destinoGasto && porteirasDa(ficha).some((p) => p.id === destinoGasto) ? destinoGasto : undefined
    mudarFicha(ficha.id, (f) => { f.custos.push({ id: novoId(), descricao: descricao.trim(), valor: v, ...(destinoId ? { destinoId } : {}) }) })
    setDescricao(''); setValor('')
  }

  async function fecharCusto() {
    const aviso = fe.comissaoPendente ? ' A comissão do comprador ainda não foi lançada e fica fora do total.' : ''
    if (!await confirmar({
      titulo: `Marcar o custo da ficha nº ${ficha.numero} como fechado?`,
      texto: `${fe.semComissao ? 'Total' : 'Custo total'}: ${reais(fe.total)}.${aviso}`, sim: 'Custo fechado',
    })) return
    mudarFicha(ficha.id, (f) => {
      const em = agora()
      f.custoFechado = { em, por: quem, semComissao: totalSemComissao(f, calcular(f)) }
      f.historico.push({ em, oque: `Custo fechado${quem ? ' por ' + quem : ''}` })
    })
    avisar('Custo fechado')
  }

  async function reabrirCusto() {
    const m = await pedirMotivoNaJanela({ titulo: 'Reabrir o custo desta ficha?', texto: 'Ela volta a ficar para conferir. O motivo fica no histórico.', sim: 'Reabrir o custo', atalhos: [] })
    if (m === null) return
    mudarFicha(ficha.id, (f) => {
      // não apaga: o fechamento anterior fica no histórico, com o motivo da reabertura
      f.custoFechado = null
      f.historico.push({ em: agora(), oque: `Custo reaberto${quem ? ' por ' + quem : ''}: ${m}` })
    })
    avisar('Custo reaberto')
  }

  /** Imprime o fechamento; o título vira o nome do PDF salvo. */
  function imprimir() {
    const t = document.title
    document.title = nome
    window.print()
    setTimeout(() => { document.title = t }, 1000)
  }

  function excel() {
    baixar(montarXlsx(abasDoFechamento(ficha, fe, apartando ? ap : undefined)), nome + '.xlsx', XLSX)
    avisar('Planilha salva em Downloads')
  }

  const eventos = ficha.historico.filter((h) => h.oque.startsWith('Custo ') || h.oque.startsWith('Gasto anulado'))
  const emitente = ficha.emitente?.nome || ajustes.emitente.nome
  const anulados = (ficha.custos ?? []).filter((x) => x.anuladoEm)

  return (
    <div>
      <div className="nao-imprimir">
        <Tarja tipo="ok">Uso interno: nada desta aba aparece no romaneio do vendedor.{fe.semComissao ? '' : ' As comissões só o gestor e a Consulta veem.'}</Tarja>

        <SituacaoDoCusto ficha={ficha} fechado={fechado} podeGravar={podeGravar} fechar={() => { void fecharCusto() }} reabrir={() => { void reabrirCusto() }} comprador={fe.semComissao} />

        <h2 className="secao">O que pagar e para quem</h2>
        <div className="cartao">
          {venda && (
            <div className="item">
              <div className="item-topo"><span className="item-titulo">Comprador · {ficha.produtor.nome || '—'}</span>
                <span className="item-valor" style={GRANDE}>{r.temPreco ? reais(r.liquido) : '—'}</span></div>
              <div className="item-baixo"><span>Líquido do romaneio — o comprador paga a você</span>
                {r.temPreco && (fe.vendedor.quitado ? <span className="etiqueta ok">recebido</span> : <span className="etiqueta aberta">falta receber {reais(fe.vendedor.aPagar)}</span>)}</div>
            </div>
          )}
          {!venda && <div className="item">
            <div className="item-topo"><span className="item-titulo">Vendedor · {ficha.produtor.nome || '—'}</span>
              <span className="item-valor" style={GRANDE}>{r.temPreco ? reais(fe.vendedor.valor - fe.vendedor.aCargoDoCliente) : '—'}</span></div>
            <div className="item-baixo">
              <span>{fe.vendedor.aCargoDoCliente > 0 ? `Sua parte do líquido (romaneio ${reais(fe.vendedor.valor)})` : `Líquido do romaneio${r.financeiro || r.funrural ? ` (bruto ${reais(r.bruto)} − descontos)` : ''}`}</span>
              {r.temPreco && (fe.vendedor.quitado
                ? <span className="etiqueta ok">pago</span>
                : <span className="etiqueta aberta">falta {reais(fe.vendedor.aPagar)}</span>)}
            </div>
            {fe.vendedor.pago > 0 && !fe.vendedor.quitado && <div className="mini-dica">Já pago: {reais(fe.vendedor.pago)}</div>}
            {/* 3F: intermediação — a parte das porteiras Cliente o cliente paga direto ao vendedor */}
            {fe.vendedor.aCargoDoCliente > 0 && (
              <div className="mini-dica"><span className="riscado">{dv.doCliente.map((c) => `${c.nome} ${reais(c.valor)}`).join(' · ')}</span> — a cargo do cliente (paga direto ao vendedor).</div>
            )}
          </div>}

          {fe.comissao && (
            <div className="item">
              <div className="item-topo"><span className="item-titulo">{NOME_COMISSAO_COMPRADOR}{comissao?.beneficiario ? ` · ${comissao.beneficiario}` : ''}</span><span className="item-valor" style={GRANDE}>{reais(fe.comissao.valor)}</span></div>
              <div className="item-baixo">
                <span>{reais(fe.comissao.porCabeca)} por cabeça × {fe.comissao.cab} cab</span>
                {fe.comissao.acertada ? <span className="etiqueta ok">paga no acerto</span>
                  : fe.comissao.falta === 0 ? <span className="etiqueta ok">paga</span>
                    : <span className="etiqueta aberta">{fe.comissao.pago > 0 ? `paga ${reais(fe.comissao.pago)} · ` : ''}falta {reais(fe.comissao.falta)}</span>}
              </div>
            </div>
          )}
          {fe.comissaoPendente && ficha.pesadoPorGestor !== true && (
            <div className="item">
              <div className="item-topo"><span className="item-titulo">{NOME_COMISSAO_COMPRADOR}</span><span className="etiqueta aberta">pendente</span></div>
              <div className="item-baixo"><span>{papel === 'gestor' ? 'Lance o valor por cabeça na aba Pagamento.' : 'O gestor ainda não lançou.'} Fica fora do total até lá.</span></div>
            </div>
          )}

          {(ficha.custos ?? []).filter((x) => !x.anuladoEm).map((x) => (
            <LinhaDoGasto key={x.id} ficha={ficha} g={x} sua={fe.doGestor[x.id] ?? x.valor} onde={apartando ? (nomeDaPorteiraDe(x.destinoId) ? `porteira ${nomeDaPorteiraDe(x.destinoId)}` : 'sem porteira — gastos rateados pelas cabeças') : 'gasto da ficha, valor total'}
              podePagar={podePagar} podeAnular={podeLancar} quem={quem} />
          ))}
          {anulados.map((x) => (
            <div className="item" key={x.id} style={{ opacity: 0.6 }}>
              <div className="item-topo"><span className="item-titulo riscado">{x.descricao}</span><span className="item-valor riscado">{reais(x.valor)}</span></div>
              <div className="item-baixo"><span>Anulado {x.anuladoEm ? quando(x.anuladoEm) : ''}{x.anuladoPor ? ` por ${x.anuladoPor}` : ''}: {x.anuladoMotivo}</span><span className="etiqueta cancelada">anulado</span></div>
            </div>
          ))}

          {podeLancar && (
            <>
              <div className="linha fim">
                <div style={{ flex: 2 }}><label className="campo"><span>Gasto</span>
                  <input value={descricao} onChange={(e) => setDescricao(e.target.value)} list="gastos-comuns" placeholder="frete, vacina…" /></label></div>
                <label className="campo"><span>Valor total (R$)</span>
                  <input inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') lancar() }} /></label>
              </div>
              {/* 3B: o frete de cada caminhão vai para a porteira dele */}
              {apartando && (
                <Escolha rotulo="De que porteira?" valor={destinoGasto} mudar={setDestinoGasto}
                  opcoes={[['', 'Sem porteira — dividir pelas cabeças'], ...ap.porteiras.map((p): [string, string] => [p.id ?? '', p.nome])]} />
              )}
              {/* sem «Comissão» na lista: ela mora na aba Pagamento, lançada pelo gestor */}
              <datalist id="gastos-comuns">{['Frete', 'Vacina', 'Taxa da GTA', 'Pedágio', 'Diária de peão'].map((g) => <option key={g} value={g} />)}</datalist>
              <button className="botao fantasma" onClick={lancar}>+ Lançar gasto</button>
            </>
          )}
          {fechado && podeGravar && <div className="mini-dica">Custo fechado: para lançar ou anular um gasto, reabra o custo. Marcar como pago continua livre.</div>}
        </div>
        {comissaoNosGastos && <Tarja>Há um gasto com «comissão» no nome e também a comissão da aba Pagamento. Confira se ela não está contada duas vezes.</Tarja>}
        {!r.temPreco && <Dica>Sem preço na negociação, o custo considera só {fe.semComissao ? 'os gastos lançados' : 'a comissão e os gastos lançados'}.</Dica>}

        {venda && r.temPreco && (
          <div className="resultado">
            <div className="tl"><span>Venda (líquido do romaneio)</span><b>{reais(r.liquido)}</b></div>
            <div className="tl"><span>− Frete e gastos</span><b>{reais(fe.totalGastos)}</b></div>
            {fe.comissao && <div className="tl"><span>− Comissão do comprador</span><b>{reais(fe.comissao.valor)}</b></div>}
            <div className="tl total"><span>Resultado da venda</span><b>{reais(resultadoDaVenda)}</b></div>
            {r.cab > 0 && <div className="tl"><span>Por cabeça ({r.cab} cab)</span><b>{reais(resultadoDaVenda / r.cab)}</b></div>}
          </div>
        )}
        {!venda && <div className="resultado">
          <div className="tl total" style={{ borderTop: 0, marginTop: 0, paddingTop: 4 }}><span>{fe.semComissao ? 'Total' : fe.comissaoPendente && ficha.pesadoPorGestor !== true ? 'Custo total (sem a comissão, pendente)' : 'Custo total da compra'}</span><b>{reais(fe.total)}</b></div>
          {/* R2B: o custo do gestor separado do que é do cliente */}
          {temCliente && <div className="tl"><span>{fe.semComissao ? 'Sua parte' : 'Custo do gestor (sua parte)'}</span><b>{reais(fe.custoDoGestor)}</b></div>}
          {temCliente && <div className="tl"><span>Custo do cliente (conferência — ele paga direto)</span><b>{reais(fe.custoDoCliente)}</b></div>}
          <div className="tl"><span>Falta pagar (sua parte)</span><b>{reais(fe.aPagar)}</b></div>
          <div className="tl"><span>Por cabeça ({r.cab} cab)</span><b>{fe.porCabeca === null ? '—' : reais(fe.porCabeca)}</b></div>
          {fe.porArroba !== null && <div className="tl"><span>Por arroba ({num(r.arrobas, 2)} @)</span><b>{reais(fe.porArroba)}</b></div>}
          {fe.porKg !== null && <div className="tl"><span>Por kg ({kg(r.kg)} kg)</span><b>{reais(fe.porKg)}</b></div>}
          {r.cab > 0 && fe.porArroba === null && <div className="mini-dica">Por arroba só quando todos os lotes são por arroba.</div>}
        </div>}

        {apartando && (
          <>
            <h2 className="secao">Por porteira</h2>
            {ap.gastosSemPorteira > 0 && <Tarja>Gastos rateados: os sem porteira ({reais(ap.gastosSemPorteira)}) foram divididos pelas cabeças de cada porteira. Se cada caminhão teve o seu frete, lance cada um na porteira dele.</Tarja>}
            {ap.porteiras.map((p) => (
              <BlocoDaPorteira key={p.id ?? 'unica'} p={p} papel={papel} semComissao={fe.semComissao} verVenda={verVenda}
                recebido={p.id && p.venda ? situacaoDoRecebimento(p.venda.valor, recebimentosDaPorteira(pg, ficha.id, p.id)) : null}
                irRomaneio={() => ir({ tela: 'ficha', id: ficha.id, aba: 'romaneio' })} />
            ))}
            <div className="mini-dica">{ap.confere
              ? 'Conferido: a soma das porteiras dá o romaneio do vendedor e os gastos da ficha, ao centavo.'
              : 'Atenção: a soma das porteiras não bateu com o romaneio. Avise quem cuida do app.'}</div>
          </>
        )}

        <div className="barra">
          <button className="botao" onClick={imprimir}>PDF / imprimir</button>
          <button className="botao fantasma" onClick={excel}>Excel</button>
        </div>
        <div className="dica">PDF: toque em <b>PDF / imprimir</b> e escolha <b>Salvar como PDF</b>. O arquivo já sai com o nome da ficha.
          {ap.porteiras.some((p) => p.porteira?.tipo === 'cliente') ? ' O papel de cada cliente fica no passo Romaneio, junto com o de compra.' : ''}</div>

        {/* 3E: a marca «Entra no meu estoque» saiu da Negociação e veio para cá:
            depois do encerramento, só o gestor, só com a ferramenta Catira ligada */}
        {comCatira && <CatiraNaFicha ficha={ficha} ir={ir} />}

        {eventos.length > 0 && (
          <>
            <h2 className="secao">Histórico do custo</h2>
            <div className="cartao">{eventos.map((h, i) => <div className="mini-dica" key={i}>{quando(h.em)} — {h.oque}</div>)}</div>
          </>
        )}
      </div>
      <Folha>
        <PapelDoFechamento ficha={ficha} fe={fe} ap={apartando ? ap : null} emitente={emitente} pendente={pendente} />
      </Folha>
    </div>
  )
}

/**
 * R2B · Um frete ou gasto: o valor, a sua parte (o da porteira Cliente é do
 * cliente, riscado), para quem, quando vence, quando foi pago e o comprovante.
 * Errado: ANULA com motivo (fica riscado, fora das somas) — nada some.
 */
function LinhaDoGasto({ ficha, g, sua, onde, podePagar, podeAnular, quem }: {
  ficha: Ficha; g: Gasto; sua: number; onde: string; podePagar: boolean; podeAnular: boolean; quem: string
}) {
  const [aberto, setAberto] = useState(false)
  const [favorecido, setFavorecido] = useState(g.favorecido ?? '')
  const [vencimento, setVencimento] = useState(g.vencimento ?? '')
  const [pagoEm, setPagoEm] = useState(g.pagoEm ?? '')
  const [conta, setConta] = useState(g.contaOrigem ?? '')
  const [guardando, setGuardando] = useState(false)
  const doServidor = usarComprovantesDoServidor()
  // o comprovante do gasto mora na ficha (de_tipo «ficha»): o da ficha que tem o id dele
  const comprovantes = comprovantesDe(doServidor, 'ficha', ficha.id, g.comprovantes ?? []).filter((c) => (g.comprovantes ?? []).some((x) => x.id === c.id))
  const doCliente = sua === 0
  const mudarGasto = (fn: (x: Gasto) => void) => mudarFicha(ficha.id, (f) => { const x = f.custos.find((y) => y.id === g.id); if (x) fn(x) })

  function salvar() {
    mudarGasto((x) => {
      const definir = (k: 'favorecido' | 'vencimento' | 'pagoEm' | 'contaOrigem', v: string) => { if (v.trim()) x[k] = v.trim(); else delete x[k] }
      definir('favorecido', favorecido); definir('vencimento', vencimento); definir('pagoEm', pagoEm); definir('contaOrigem', conta)
    })
    avisar('Gasto atualizado')
    setAberto(false)
  }
  async function anular() {
    const m = await pedirMotivoNaJanela({ titulo: `Anular o gasto «${g.descricao}» de ${reais(g.valor)}?`, texto: 'Ele continua na lista, riscado, fora das somas. O motivo fica no histórico.', sim: 'Anular o gasto', atalhos: ['lançado em dobro', 'valor errado'], perigo: true })
    if (m === null) return
    mudarFicha(ficha.id, (f) => {
      const x = f.custos.find((y) => y.id === g.id)
      if (!x) return
      x.anuladoEm = agora(); x.anuladoMotivo = m; if (quem) x.anuladoPor = quem
      f.historico.push({ em: agora(), oque: `Gasto anulado: ${g.descricao} ${reais(g.valor)} — ${m}` })
    })
    avisar('Gasto anulado')
  }
  async function anexar(arquivos: FileList | null) {
    if (!arquivos?.length) return
    setGuardando(true)
    const novos: Comprovante[] = []
    try {
      for (const a of Array.from(arquivos)) {
        const id = novoId()
        const guardado = await guardarArquivo(id, a)
        novos.push({ id, nome: a.name, tipo: guardado.type, tamanho: guardado.size })
      }
    } catch (e) {
      window.alert(e instanceof ArquivoRecusado ? e.message : 'Não foi possível guardar o arquivo neste aparelho.')
    } finally {
      if (novos.length) { mudarGasto((x) => { x.comprovantes = [...(x.comprovantes ?? []), ...novos] }); avisar('Comprovante anexado') }
      setGuardando(false)
    }
  }

  return (
    <div className="item">
      <div className="item-topo">
        <span className={`item-titulo${doCliente ? ' riscado' : ''}`}>{g.descricao}{g.favorecido ? ` · ${g.favorecido}` : ''}</span>
        <span className={`item-valor${doCliente ? ' riscado' : ''}`} style={GRANDE}>{reais(doCliente ? g.valor : sua)}</span>
      </div>
      <div className="item-baixo">
        <span>{onde}{!doCliente && sua !== g.valor ? ` · sua parte de ${reais(g.valor)}` : ''}{g.vencimento ? ` · vence ${dataBR(g.vencimento)}` : ''}</span>
        {doCliente ? <span className="etiqueta neutra">a cargo do cliente</span>
          : g.pagoEm ? <span className="etiqueta ok">pago {dataBR(g.pagoEm)}</span> : <span className="etiqueta aberta">a pagar</span>}
      </div>
      {g.contaOrigem && <div className="mini-dica">Conta de origem: {g.contaOrigem}</div>}
      {comprovantes.length > 0 && (
        <div className="comprovantes">{comprovantes.map((c) => (
          <button key={c.id} className="ligacao" onClick={async () => { const erro = await abrirComprovante(c.id); if (erro) window.alert(erro) }}>📎 {c.nome}</button>
        ))}</div>
      )}
      {(podePagar || podeAnular) && !doCliente && (
        <div className="linha" style={{ marginTop: 4 }}>
          {podePagar && <button className="mini" onClick={() => setAberto(!aberto)}>{g.pagoEm ? 'Pagamento e detalhes' : 'Marcar pago / detalhes'}</button>}
          {podePagar && (
            <label className="mini anexar" style={{ cursor: 'pointer' }}>{guardando ? 'Guardando…' : '📎 Comprovante'}
              <input type="file" accept="image/*,application/pdf" multiple hidden onChange={(e) => { void anexar(e.target.files); e.target.value = '' }} /></label>
          )}
          {podeAnular && <button className="mini" onClick={() => { void anular() }}>Anular</button>}
        </div>
      )}
      {doCliente && podeAnular && <div className="linha" style={{ marginTop: 4 }}><button className="mini" onClick={() => { void anular() }}>Anular</button></div>}
      {aberto && (
        <div style={{ borderTop: '1px dashed var(--linha)', paddingTop: 8, marginTop: 8 }}>
          <div className="linha">
            <Campo rotulo="Para quem (favorecido)" valor={favorecido} mudar={setFavorecido} dica="ex.: Transportadora Rio Verde" />
            <Campo rotulo="Vencimento" tipo="date" valor={vencimento} mudar={setVencimento} />
          </div>
          <div className="linha">
            <Campo rotulo="Pago em" tipo="date" valor={pagoEm} mudar={setPagoEm} />
            <Campo rotulo="Conta de origem (opcional)" valor={conta} mudar={setConta} dica="ex.: Sicredi PJ" />
          </div>
          <div className="barra">
            {!pagoEm && <button className="botao fantasma" onClick={() => setPagoEm(hoje())}>Pago hoje</button>}
            <button className="botao" onClick={salvar}>Salvar</button>
          </div>
          <div className="mini-dica">Deixe «Pago em» vazio enquanto não pagar: o gasto fica «a pagar» nas Fichas e na tela Contas.</div>
        </div>
      )}
    </div>
  )
}

/** 3B · Um bloco por porteira: o que ela custou e, conforme o tipo, o cliente, a catira ou a revenda. */
function BlocoDaPorteira({ p, papel, semComissao, verVenda, recebido, irRomaneio }: {
  p: ContaDaPorteira; papel: string; semComissao: boolean; verVenda: boolean
  recebido: ReturnType<typeof situacaoDoRecebimento> | null
  irRomaneio: () => void
}) {
  const tipo = p.porteira?.tipo
  // 3C: sem Catira não se fala em estoque da catira; sem «Comissões e pagamentos»
  // não se cobra o lançamento da comissão do cliente (o valor lançado continua na conta)
  const comCatira = usarFerramenta('catira')
  const comComissoes = usarFerramenta('comissoes')
  return (
    <div className="cartao">
      <div className="item-topo">
        <b>{p.nome}</b>
        {tipo && <span className="etiqueta neutra">{TIPOS_DE_PORTEIRA[tipo].nome}</span>}
      </div>
      <div className="mini-dica" style={{ marginTop: 0 }}>{p.cab} cab{p.kg ? ` · ${kg(p.kg)} kg` : ''}{p.temArroba ? ` · ${num(p.arrobas, 2)} @` : ''}</div>
      <div className="tl"><span>Vendedor (desta porteira){p.financeiro || p.funrural ? ` — bruto ${reais(p.bruto)} − descontos` : ''}</span><b>{p.temPreco ? reais(p.liquido) : '—'}</b></div>
      {p.gastos.map((g) => <div className="tl" key={g.id}><span>{g.descricao}</span><b>{reais(g.valor)}</b></div>)}
      {p.gastosRateados > 0 && <div className="tl"><span>Gastos rateados</span><b>{reais(p.gastosRateados)}</b></div>}
      {p.comissaoPagar && <div className="tl"><span>{NOME_COMISSAO_COMPRADOR} · {reais(p.comissaoPagar.porCabeca)} × {p.comissaoPagar.cab}</span><b>{reais(p.comissaoPagar.valor)}</b></div>}
      <div className="tl total"><span>{semComissao ? 'Total desta porteira' : 'Custo desta porteira'}</span><b>{reais(p.custo)}</b></div>
      <div className="tl"><span>Por cabeça</span><b>{p.porCabeca === null ? '—' : reais(p.porCabeca)}</b></div>
      {p.porArroba !== null && <div className="tl"><span>Por arroba</span><b>{reais(p.porArroba)}</b></div>}

      {tipo === 'cliente' && (
        <>
          {p.comissaoReceber && (
            <div className="tl"><span>{NOME_COMISSAO_CLIENTE} · {reais(p.comissaoReceber.porCabeca)} × {p.comissaoReceber.cab}</span><b>{reais(p.comissaoReceber.valor)}</b></div>
          )}
          {comComissoes && p.comissaoReceberPendente && (
            <div className="tl"><span>{NOME_COMISSAO_CLIENTE}</span><span className="etiqueta aberta">pendente</span></div>
          )}
          {comComissoes && p.comissaoReceberPendente && <div className="mini-dica">{papel === 'gestor' ? 'Lance o valor por cabeça na aba Pagamento.' : 'O gestor ainda não lançou.'}</div>}
          {/* 3F: intermediação — quem paga o vendedor é o cliente, direto */}
          <div className="mini-dica">{semComissao
            ? `A fazenda paga o vendedor direto (${reais(p.liquido)}).`
            : `Intermediação: o valor ao vendedor (${reais(p.liquido)}) e o frete desta porteira o cliente paga direto — ficam a cargo do cliente, fora do que você paga.`}</div>
          <button className="botao fantasma pequeno" onClick={irRomaneio}>Papel do cliente (passo Romaneio)</button>
        </>
      )}
      {/* o controle do estoque é só do gestor: o comprador nem fica sabendo dele */}
      {tipo === 'estoque' && !semComissao && comCatira && <div className="mini-dica">É este custo (sem comissão de cliente) que entra no Meu estoque, se o gestor marcar a ficha.</div>}
      {/* 3F: o bloco da Revenda — só gestor e Consulta; o comprador não vê venda nem ágio */}
      {tipo === 'revenda' && verVenda && !semComissao && <BlocoDaRevenda p={p} recebido={recebido} irRomaneio={irRomaneio} />}
    </div>
  )
}

/**
 * 3F · Compra, venda, frete e gastos, comissão do comprador e ÁGIO, no total,
 * por cabeça e por arroba (a arroba da compra — a mesma régua em todas as linhas).
 */
function BlocoDaRevenda({ p, recebido, irRomaneio }: { p: ContaDaPorteira; recebido: ReturnType<typeof situacaoDoRecebimento> | null; irRomaneio: () => void }) {
  if (!p.venda) return <div className="mini-dica">Sem preço de venda ainda: monte a tabela em Negociação › porteira Revenda › «Preço de venda» para ver o ágio.</div>
  const linhas = linhasDaRevenda(p)
  const temArroba = linhas.some((l) => l.porArroba !== null)
  const celula = { textAlign: 'right', padding: '4px 4px', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' } as const
  return (
    <div style={{ marginTop: 8 }}>
      <div className="mini-dica"><b>Revenda</b>{p.venda.conta.modo === 'total' ? ' — venda por valor total (porteira antiga); ponha preço por lote na Negociação.' : ''}</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
          <thead><tr><th style={{ textAlign: 'left' }} />
            <th style={celula}>Total</th><th style={celula}>Por cabeça</th>{temArroba && <th style={celula}>Por arroba</th>}</tr></thead>
          <tbody>{linhas.map((l) => (
            <tr key={l.rotulo} style={l.rotulo === 'Ágio' ? { fontWeight: 800, borderTop: '1px solid var(--linha)' } : undefined}>
              <td style={{ padding: '4px 4px' }}>{l.rotulo}</td>
              <td style={celula}>{l.total === null ? '—' : reais(l.total)}</td>
              <td style={celula}>{l.porCabeca === null ? '—' : reais(l.porCabeca)}</td>
              {temArroba && <td style={celula}>{l.porArroba === null ? '—' : reais(l.porArroba)}</td>}
            </tr>
          ))}</tbody>
        </table>
      </div>
      {p.venda.conta.semPreco.length > 0 && <Tarja>Sem preço de venda: {p.venda.conta.semPreco.join(', ')}. Esses animais ficam fora da venda e do ágio.</Tarja>}
      {recebido && p.venda.valor !== null && (
        <div className="tl"><span>Recebido do comprador final {reais(recebido.recebido)}</span>
          {recebido.quitado ? <span className="etiqueta ok">recebido</span> : <span className="etiqueta aberta">falta {reais(recebido.aReceber)}</span>}</div>
      )}
      {/* R2B: o ágio sai, sim, num papel — o Fechamento interno (este), que nunca vai ao comprador final nem ao vendedor */}
      <div className="mini-dica">O ágio sai só no Fechamento interno (o PDF e o Excel desta aba), que não se manda ao comprador final nem ao vendedor.
        O romaneio de venda (para o comprador final) fica no passo Romaneio e não mostra o ágio.
        {' '}<button className="mini" onClick={irRomaneio}>Abrir o romaneio de venda</button></div>
    </div>
  )
}

function SituacaoDoCusto({ ficha, fechado, podeGravar, fechar, reabrir, comprador }: {
  ficha: Ficha; fechado: boolean; podeGravar: boolean; fechar: () => void; reabrir: () => void; comprador: boolean
}) {
  if (ficha.situacao === 'aberta') return <Dica>A ficha está em andamento. O custo se fecha depois de encerrar a pesagem.</Dica>
  if (ficha.situacao !== 'encerrada') return null
  const cf = ficha.custoFechado
  if (fechado && cf) {
    return (
      <div className="cartao">
        <div className="item-topo"><b>Custo fechado</b><span className="etiqueta ok">fechado</span></div>
        <div className="mini-dica">{quando(cf.em)}{cf.por ? ` por ${cf.por}` : ''}</div>
        {podeGravar && <button className="botao fantasma pequeno" onClick={reabrir}>Reabrir o custo</button>}
      </div>
    )
  }
  return (
    <div className="cartao">
      {/* R1C: «custo pendente» não aparece para o comprador */}
      <div className="item-topo"><b>{comprador ? 'Conferir o custo' : 'Custo pendente'}</b></div>
      {cf
        ? <div className="mini-dica">Foi fechado {quando(cf.em)}{cf.por ? ` por ${cf.por}` : ''}, mas o romaneio ou os gastos mudaram depois. Confira e feche de novo.</div>
        : <div className="mini-dica">Confira o que pagar e para quem e toque em «Custo fechado».</div>}
      {podeGravar
        ? <button className="botao" onClick={fechar}>Custo fechado</button>
        : <div className="mini-dica">Quem fecha é o gestor ou o comprador.</div>}
    </div>
  )
}

/** O papel do fechamento: branco, imprime como o romaneio. O do comprador sai sem comissão. */
function PapelDoFechamento({ ficha, fe, ap, emitente, pendente }: { ficha: Ficha; fe: Fechamento; ap: Apartacao | null; emitente: string; pendente: boolean }) {
  const r = calcular(ficha)
  const nomeDe = (id?: string) => ap?.porteiras.find((p) => p.id === id)?.nome
  const temCliente = fe.vendedor.aCargoDoCliente > 0 || fe.custoDoCliente > 0
  return (
    <div className="papel">
      <div className="p-cabeca">
        <div><h3>Fechamento da compra — uso interno</h3><div className="p-sub">{emitente}</div></div>
        <div className="p-numero"><b>Ficha {ficha.numero}</b><br />{periodo(ficha)}</div>
      </div>
      {/* R1C: o carimbo de pendente não aparece para o comprador */}
      {pendente && !fe.semComissao && <div className="carimbo">CUSTO PENDENTE — AINDA NÃO CONFERIDO</div>}
      <div className="p-linha"><b>Vendedor:</b> {ficha.produtor.nome} · <b>Origem:</b> {lugar(ficha.produtor) || '—'} → <b>{rotuloDoDestino(ficha)}:</b> {destinoInterno(ficha) || '—'}</div>
      <div className="p-linha"><b>Animais:</b> {r.cab} cab{r.kg ? ` · ${kg(r.kg)} kg` : ''}{r.temArroba ? ` · ${num(r.arrobas, 2)} @` : ''}</div>

      <h4>O que pagar e para quem</h4>
      <table>
        <thead><tr><th>Para quem</th><th>O que é</th><th className="n">Valor</th><th className="n">Sua parte</th><th className="n">Já pago</th><th className="n">Falta</th></tr></thead>
        <tbody>
          <tr>
            <td>Vendedor — {ficha.produtor.nome}</td>
            <td>Líquido do romaneio (bruto {reais(r.bruto)}{!r.descontosIguais ? ' − descontos por lote'
              : <>{r.financeiro ? ` − desconto à vista ${num(r.financeiroPct ?? 0, 2)}%` : ''}{r.funrural ? ` − Funrural ${num(r.funruralPct ?? 0, 2)}%` : ''}</>})</td>
            <td className="n">{reais(fe.vendedor.valor)}</td><td className="n">{reais(fe.vendedor.valor - fe.vendedor.aCargoDoCliente)}</td>
            <td className="n">{reais(fe.vendedor.pago)}</td><td className="n">{reais(fe.vendedor.aPagar)}</td>
          </tr>
          {/* R2B: a linha «a cargo do cliente» — não é dinheiro do gestor */}
          {fe.vendedor.aCargoDoCliente > 0 && (
            <tr><td>A cargo do cliente</td><td>parte do vendedor que o cliente paga direto (intermediação)</td>
              <td className="n">{reais(fe.vendedor.aCargoDoCliente)}</td><td className="n">—</td><td className="n">—</td><td className="n">—</td></tr>
          )}
          {fe.comissao && (
            <tr>
              <td>{NOME_COMISSAO_COMPRADOR}</td><td>{reais(fe.comissao.porCabeca)} por cabeça × {fe.comissao.cab} cab</td>
              <td className="n">{reais(fe.comissao.valor)}</td><td className="n">{reais(fe.comissao.valor)}</td>
              <td className="n">{fe.comissao.pago ? reais(fe.comissao.pago) : '—'}</td>
              <td className="n">{reais(fe.comissao.falta)}</td>
            </tr>
          )}
          {fe.comissaoPendente && ficha.pesadoPorGestor !== true && <tr><td>{NOME_COMISSAO_COMPRADOR}</td><td colSpan={5}>pendente — ainda não lançada, fora do total</td></tr>}
          {fe.gastos.map((x) => {
            const sua = fe.doGestor[x.id] ?? x.valor
            return (
              <tr key={x.id}><td>{x.descricao}{x.favorecido ? ` — ${x.favorecido}` : ''}</td>
                <td>gasto da ficha (valor total){ap ? ` — ${nomeDe(x.destinoId) ?? 'sem porteira, gastos rateados'}` : ''}{sua === 0 ? ' — a cargo do cliente' : ''}</td>
                <td className="n">{reais(x.valor)}</td><td className="n">{reais(sua)}</td>
                <td className="n">{x.pagoEm && sua ? reais(sua) : '—'}</td><td className="n">{x.pagoEm || !sua ? reais(0) : reais(sua)}</td></tr>
            )
          })}
        </tbody>
      </table>

      <table className="p-valores" style={{ minWidth: '60%' }}><tbody>
        <tr className="liquido"><td>{fe.semComissao ? 'Total' : 'Custo total da compra'}</td><td className="n">{reais(fe.total)}</td></tr>
        {temCliente && <tr><td>{fe.semComissao ? 'Sua parte' : 'Custo do gestor (sua parte)'}</td><td className="n">{reais(fe.custoDoGestor)}</td></tr>}
        {temCliente && <tr><td>Custo do cliente (conferência)</td><td className="n">{reais(fe.custoDoCliente)}</td></tr>}
        <tr><td>Falta pagar (sua parte)</td><td className="n">{reais(fe.aPagar)}</td></tr>
        {fe.porCabeca !== null && <tr><td>Por cabeça</td><td className="n">{reais(fe.porCabeca)}</td></tr>}
        {fe.porArroba !== null && <tr><td>Por arroba</td><td className="n">{reais(fe.porArroba)}</td></tr>}
        {fe.porKg !== null && <tr><td>Por kg</td><td className="n">{reais(fe.porKg)}</td></tr>}
      </tbody></table>

      {ap && (
        <>
          <h4>Por porteira</h4>
          <table>
            <thead><tr>
              <th>Porteira</th><th className="n">Cab.</th><th className="n">Vendedor</th><th className="n">Gastos</th>
              {!fe.semComissao && <th className="n">Com. comprador</th>}
              <th className="n">{fe.semComissao ? 'Total' : 'Custo'}</th><th className="n">Por cabeça</th>
              {!fe.semComissao && <th className="n">Com. cliente</th>}
            </tr></thead>
            <tbody>{ap.porteiras.map((p) => (
              <tr key={p.id ?? 'unica'}>
                <td>{p.nome}{p.porteira && p.nome !== TIPOS_DE_PORTEIRA[p.porteira.tipo].nome ? ` (${TIPOS_DE_PORTEIRA[p.porteira.tipo].nome.toLowerCase()})` : ''}</td>
                <td className="n">{p.cab}</td><td className="n">{reais(p.liquido)}</td><td className="n">{reais(p.totalGastos)}</td>
                {!fe.semComissao && <td className="n">{p.comissaoPagar ? reais(p.comissaoPagar.valor) : '—'}</td>}
                <td className="n">{reais(p.custo)}</td><td className="n">{p.porCabeca === null ? '—' : reais(p.porCabeca)}</td>
                {!fe.semComissao && <td className="n">{p.comissaoReceber ? reais(p.comissaoReceber.valor) : p.porteira?.tipo === 'cliente' ? 'pendente' : '—'}</td>}
              </tr>
            ))}</tbody>
          </table>
          {ap.gastosSemPorteira > 0 && <div className="p-nota">Gastos rateados: os sem porteira ({reais(ap.gastosSemPorteira)}) foram divididos pelas cabeças de cada porteira.</div>}
          {/* 3F: a revenda, só na versão do gestor e da Consulta (uso interno — não vai a ninguém de fora) */}
          {!fe.semComissao && ap.porteiras.filter((p) => linhasDaRevenda(p).length).map((p) => (
            <div key={'rv' + p.id}>
              <h4>Revenda — {p.porteira?.nome || p.nome}</h4>
              <table className="p-valores" style={{ minWidth: '70%' }}>
                <thead><tr><th /><th className="n">Total</th><th className="n">Por cabeça</th><th className="n">Por arroba</th></tr></thead>
                <tbody>{linhasDaRevenda(p).map((l) => (
                  <tr key={l.rotulo} className={l.rotulo === 'Ágio' ? 'liquido' : ''}><td>{l.rotulo}</td>
                    <td className="n">{l.total === null ? '—' : reais(l.total)}</td><td className="n">{l.porCabeca === null ? '—' : reais(l.porCabeca)}</td>
                    <td className="n">{l.porArroba === null ? '—' : reais(l.porArroba)}</td></tr>
                ))}</tbody>
              </table>
            </div>
          ))}
        </>
      )}
      <div className="p-rodape">
        {ficha.custoFechado ? `Custo fechado em ${quando(ficha.custoFechado.em)}${ficha.custoFechado.por ? ` por ${ficha.custoFechado.por}` : ''}. ` : ''}
        Relatório interno, não vai ao vendedor nem ao comprador final. Gerado em {quando(new Date().toISOString())}.</div>
    </div>
  )
}
